package frontend.pages

import AccountSelectByCurrency
import CurrencyPairSelect
import StatSampleRateSelect
import TradeOrderTypeSelect
import androidx.compose.runtime.*
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.DecimalMode
import com.ionspin.kotlin.bignum.decimal.RoundingMode
import components.*
import frontend.App
import frontend.components.Modal
import frontend.lib.DateTimeDisplay
import frontend.lib.NiceScale
import frontend.lib.classList
import frontend.lib.displayCurrencyCode
import frontend.models.User
import frontend.models.read
import frontend.models.write
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import net.sergeych.sprintf.sprintf
import net.sergeych.ugdx.shared.ApiException
import net.sergeych.ugdx.shared.Currency
import net.sergeych.ugdx.shared.ExchangeAccessOrder
import net.sergeych.ugdx.shared.api.ApiAccount
import net.sergeych.ugdx.shared.trader.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.*
import org.w3c.dom.events.Event
import kotlin.math.absoluteValue
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

private open class ElementBuilderImplementation2<TElement : Element>(
    private val tagName: String,
) : ElementBuilder<TElement> {
    private val el: Element by lazy {
        document.createElement(tagName)
    }

    override fun create(): TElement = el.cloneNode() as TElement
}

private val Canvas: ElementBuilder<HTMLCanvasElement> = ElementBuilderImplementation2("canvas")

private val CandleChartHeight = 450
private val candleRed = "#dc2626" // red
private val candleGreen = "#16a34a"

@Composable
fun Canvas(
    attrs: AttrBuilderContext<HTMLCanvasElement>? = null,
    content: ContentBuilder<HTMLCanvasElement>? = null
) {
    TagElement(
        elementBuilder = Canvas,
        applyAttrs = attrs,
        content = content
    )
}

data class ChartSize(
    val width: Int,
    val height: Int
)

data class ChartPeriod(
    val from: Instant,
    val to: Instant
)

sealed class Candle(val start: Instant) {
    class Empty(start: Instant) : Candle(start)
    class Full(start: Instant, val open: Double, val close: Double, val min: Double, val max: Double) : Candle(start)
}

@Composable
fun CandleChartOverlay(
    size: ChartSize,
    candles: List<Candle>,
    chartWidth: Double,
    chartHeight: Double,
    chartX: (x: Instant) -> Double,
    chartY: (y: Double) -> Double
) {
    val scope = rememberCoroutineScope()
    var mouseX: Double? by remember { mutableStateOf(null) }
    var mouseY: Double? by remember { mutableStateOf(null) }
    val canvasWidth = (size.width * 2).toDouble()
    val canvasHeight = (size.height * 2).toDouble()
    val borderColor = "#E0E3EB"
    var job = scope.launch {}
    val infoColor = "#3b82f6"
    val yAxisWidth = 140.0
    val xAxisHeight = 70.0
    val yAxisLegendHeight = 40.0

    data class ClosestCandle(val d: Double, val c: Candle)

    Canvas(attrs = {
        attr("data-key", "${candles.last()}") // need to trigger recompose
        style {
            position(Position.Absolute)
            left(0.percent)
            top(0.percent)
            width(size.width.px)
            height(size.height.px)
        }
    }) {
        DomSideEffect { canvas ->
            canvas.width = canvasWidth.toInt()
            canvas.height = canvasHeight.toInt()
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble());

            canvas.onmouseleave = { e ->
                ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble());
            }

            val infoWidth = 200.0
            val infoHeight = 135.0
            fun drawInfo(candle: Candle.Full, cornerX: Double, cornerY: Double) {
                ctx.fillStyle = "#93c5fd"
                ctx.fillRect(cornerX, cornerY, infoWidth, infoHeight)

                fun drawLine(level: Int, value: String) {
                    ctx.font = "20px sans-serif"
                    ctx.fillStyle = "black"
                    ctx.textAlign = CanvasTextAlign.LEFT
                    ctx.fillText(
                        value,
                        cornerX + 20.0,
                        cornerY + 30.0 + level * 30.0
                    )
                }

                drawLine(0, "Open: ${candle.open.toString().substring(0, 10)}")
                drawLine(1, "High: ${candle.max.toString().substring(0, 10)}")
                drawLine(2, "Low: ${candle.min.toString().substring(0, 10)}")
                drawLine(3, "Close: ${candle.close.toString().substring(0, 10)}")
            }

            canvas.onmousemove = { e ->
                val mouseX = e.offsetX * 2
                val mouseY = e.offsetY * 2

                window.requestAnimationFrame {
                    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble());

                    val closest = candles.map {
                        ClosestCandle((chartX(it.start) - mouseX).absoluteValue, it)
                    }.minByOrNull { it.d }

                    closest?.let {
                        val candle = it.c
                        val candleTime = candle.start
                        val candleX = chartX(candleTime)

                        ctx.strokeStyle = infoColor
                        ctx.lineWidth = 2.0
                        ctx.setLineDash(arrayOf(2.0, 2.0))
                        ctx.beginPath()

                        ctx.moveTo(candleX, 0.0)
                        ctx.lineTo(candleX, chartHeight + yAxisLegendHeight + 4)
                        ctx.stroke()

                        ctx.fillStyle = infoColor
                        ctx.fillRect(
                            candleX - yAxisWidth / 2,
                            chartHeight + yAxisLegendHeight + 4,
                            yAxisWidth,
                            yAxisLegendHeight
                        )

                        val timeFormated = "%tb %te, %tH:%tM".sprintf(candleTime, candleTime, candleTime, candleTime)
                        ctx.font = "20px sans-serif"
                        ctx.fillStyle = "white"
                        ctx.textAlign = CanvasTextAlign.CENTER
                        ctx.fillText(
                            timeFormated,
                            candleX,
                            chartHeight + 70
                        )

                        if (candle is Candle.Full) {
                            var infoX = 0.0
                            var infoY = 0.0
                            val offset = 20.0
                            val infoDeltaX = infoWidth + offset
                            val infoDeltaY = infoHeight + offset
                            if (mouseX + infoDeltaX >= chartWidth) infoX = -infoDeltaX
                            else infoX = offset
                            if (mouseY + infoDeltaY >= chartHeight + yAxisLegendHeight) infoY = -infoDeltaY
                            else infoY = offset

                            ctx.strokeStyle = infoColor
                            ctx.lineWidth = 2.0
                            ctx.setLineDash(arrayOf(2.0, 2.0))
                            ctx.beginPath()
                            ctx.moveTo(0.0, chartY(candle.close))
                            ctx.lineTo(chartWidth, chartY(candle.close))
                            ctx.stroke()

                            ctx.fillStyle = infoColor
                            ctx.fillRect(
                                chartWidth,
                                chartY(candle.close) - yAxisLegendHeight / 2,
                                yAxisWidth,
                                yAxisLegendHeight
                            )

                            ctx.font = "20px sans-serif"
                            ctx.fillStyle = "white"
                            ctx.textAlign = CanvasTextAlign.CENTER
                            ctx.fillText(
                                candle.close.toString().substring(0, 10),
                                chartWidth + yAxisWidth / 2,
                                chartY(candle.close) + (yAxisLegendHeight - 10.0) / 4
                            )

                            drawInfo(candle, mouseX + infoX, mouseY + infoY)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun CandleChart(size: ChartSize, rate: StatSampleRate, dataOriginal: List<Pair<Instant, CandleStick>>) {
    val data = dataOriginal.reversed()
    val candles: List<Candle> = data.map { p ->
        val stick = p.second
        val open = stick.open
        val close = stick.close
        val max = stick.max
        val min = stick.min

        if (open != null && close != null && min != null && max != null) Candle.Full(p.first, open, close, min, max)
        else Candle.Empty(p.first)
    }
    val fullCandles = candles.filter { it is Candle.Full } as List<Candle.Full>
    val yAxisWidth = 140.0
    val xAxisHeight = 70.0
    val yAxisLegendHeight = 40.0
    val borderColor = "#E0E3EB"
    val netColor = borderColor
    val canvasWidth = (size.width * 2).toDouble()
    val canvasHeight = (size.height * 2).toDouble()
    val chartWidth = canvasWidth - yAxisWidth
    val chartHeight = canvasHeight - xAxisHeight - yAxisLegendHeight

    val maxData = fullCandles.maxByOrNull { it.max }?.max ?: 0.0
    val minData = fullCandles.minByOrNull { it.min }?.min ?: 0.0
    val deltaData = (maxData - minData) * 0.2

    val scale = NiceScale(minData - deltaData, maxData + deltaData)
    val min = scale.niceMin
    val max = scale.niceMax

    val candleWidth = canvasWidth / data.size - 10

    val from = data.first().first
    val to = data.last().first

    fun sec(time: Instant): Double {
        return time.epochSeconds.toDouble()
    }

    fun chartY(value: Double): Double {
        return (1 - (value - min) / (max - min)) * chartHeight + yAxisLegendHeight / 2
    }

    fun chartX(time: Instant): Double {
        val duration = sec(to) - sec(from)
        val factor = (sec(time) - sec(from)) / duration
        return factor * chartWidth
    }

    fun color(open: Double, close: Double): String {
        if (open == null || close == null) return "black"
        if (close > open) return candleGreen
        else if (close < open) return candleRed
        else return "black"
    }

    fun drawCandle(ctx: CanvasRenderingContext2D, candle: Candle) {
        if (candle is Candle.Full) {
            val c = candle

            // Draw high/low
            ctx.strokeStyle = color(c.open, c.close)
            ctx.lineWidth = 4.0
            ctx.beginPath()
            ctx.moveTo(chartX(c.start), chartY(c.max))
            ctx.lineTo(chartX(c.start), chartY(c.min))
            ctx.stroke()

            // Draw open/close
            if ((chartY(c.open) - chartY(c.close)).absoluteValue > 2) {
                ctx.lineWidth = candleWidth
                ctx.beginPath()
                ctx.moveTo(chartX(c.start), chartY(c.close))
                ctx.lineTo(chartX(c.start), chartY(c.open))
                ctx.stroke()
            } else {
                ctx.beginPath()
                ctx.moveTo(chartX(c.start) - candleWidth / 2, chartY(c.open))
                ctx.lineTo(chartX(c.start) + candleWidth / 2, chartY(c.open))
                ctx.stroke()
            }
        }
    }

    fun drawCloseLine(ctx: CanvasRenderingContext2D) {
        val last = fullCandles.lastOrNull()

        last?.let {
            val candleColor = color(it.open, it.close)
            ctx.strokeStyle = candleColor
            ctx.lineWidth = 2.0
            ctx.setLineDash(arrayOf(2.0, 4.0))
            ctx.beginPath()
            ctx.moveTo(0.0, chartY(it.close))
            ctx.lineTo(canvasWidth, chartY(it.close))
            ctx.stroke()

            ctx.fillStyle = candleColor
            ctx.fillRect(chartWidth, chartY(it.close) - yAxisLegendHeight / 2, yAxisWidth, yAxisLegendHeight)

            ctx.font = "20px sans-serif"
            ctx.fillStyle = "white"
            ctx.textAlign = CanvasTextAlign.CENTER
            ctx.fillText(
                it.close.toString().substring(0, 10),
                chartWidth + yAxisWidth / 2,
                chartY(it.close) + (yAxisLegendHeight - 10.0) / 4
            )
        }
    }

    fun drawYAxis(ctx: CanvasRenderingContext2D) {
        ctx.strokeStyle = borderColor
        ctx.lineWidth = 1.0
        ctx.beginPath()
        ctx.moveTo(chartWidth, 0.0)
        ctx.lineTo(chartWidth, canvasHeight)
        ctx.stroke()
        var yLevel = scale.niceMin

        while (yLevel <= scale.niceMax) {
            ctx.strokeStyle = netColor
            ctx.lineWidth = 1.0
            ctx.beginPath()
            ctx.moveTo(0.0, chartY(yLevel))
            ctx.lineTo(chartWidth, chartY(yLevel))
            ctx.stroke()

            ctx.font = "20px sans-serif"
            ctx.fillStyle = "black"
            ctx.textAlign = CanvasTextAlign.LEFT
            ctx.fillText(
                yLevel.toString().substring(0, 10),
                chartWidth + 16,
                chartY(yLevel) + (yAxisLegendHeight - 10.0) / 4
            )
            yLevel += scale.tickSpacing
        }
    }

    fun xAxisLabel(secondsAgo: Int): String {
        val minutesAgo = (secondsAgo / 60)
        val dt = to - secondsAgo.seconds

        return when (rate) {
            StatSampleRate.REALTIME -> "$secondsAgo sec ago"
            StatSampleRate.MINUTES -> "$minutesAgo min ago"
            StatSampleRate.HOURS -> "%tb %te, %tH:%tM".sprintf(dt, dt, dt, dt)
            StatSampleRate.DAYS -> "%tb %te, %tH:%tM".sprintf(dt, dt, dt, dt)
        }
    }

    fun drawXAxis(ctx: CanvasRenderingContext2D) {
        ctx.strokeStyle = borderColor
        ctx.lineWidth = 1.0
        ctx.beginPath()
        ctx.moveTo(0.0, chartHeight + yAxisLegendHeight)
        ctx.lineTo(canvasWidth, chartHeight + yAxisLegendHeight)
        ctx.stroke()

        val duration = sec(to) - sec(from)
        val totalLines = 6
        val step = (duration / totalLines).toInt()

        for (i in 1..(totalLines - 1)) {
            val xLevel = i * step
            val x = chartWidth - (xLevel / duration) * chartWidth
            ctx.strokeStyle = netColor
            ctx.lineWidth = 1.0
            ctx.beginPath()
            ctx.moveTo(x, 0.0)
            ctx.lineTo(x, chartHeight + yAxisLegendHeight)
            ctx.stroke()

            ctx.font = "20px sans-serif"
            ctx.fillStyle = "black"
            ctx.textAlign = CanvasTextAlign.CENTER
            ctx.fillText(
                xAxisLabel(xLevel),
                x,
                chartHeight + 70
            )
        }
    }

    Canvas(attrs = {
        attr("data-key", "${data.size}") // need to trigger recompose
        style {
            position(Position.Absolute)
            left(0.percent)
            top(0.percent)
            width(size.width.px)
            height(size.height.px)
        }
    }) {
        DomSideEffect { canvas ->
            canvas.width = canvasWidth.toInt()
            canvas.height = canvasHeight.toInt()
            val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
            ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble());

            drawXAxis(ctx)
            drawYAxis(ctx)
            candles.forEach { drawCandle(ctx, it) }
            drawCloseLine(ctx)
        }
    }

    CandleChartOverlay(size, candles, chartWidth, chartHeight, ::chartX, ::chartY)
}

data class CandleStickPair(val start: Instant, val stick: CandleStick)


@Composable
fun CandleChartLoader(currencyPair: CurrencyPair, rate: StatSampleRate, size: ChartSize) {
    var data by remember { mutableStateOf<List<Pair<Instant, CandleStick>>>(emptyList()) }
    val candlesTotal = 60

    LaunchedEffect(currencyPair, rate) {
        Stats.sticksFlow(currencyPair, rate, candlesTotal).collect {
            data = it
        }
    }

    Div(attrs = {
        style {
            position(Position.Relative)
            height(size.height.px)
            width(size.width.px - 9.px)
        }
    }) {
        if (data.size > 0) CandleChart(size, rate, data)
    }
}

@Composable
fun ChartChartResizer(onChange: (width: Int, height: Int) -> Unit) {
    Div(attrs = { classList("flex flex-row") }) {
        Div(attrs = { classList("basis-3/4") }) {
            Div(attrs = { classList("px-5") }) {
                Div(attrs = {
                    ref {
                        val container = it
                        val h = document.body?.offsetHeight ?: 0
                        val ch = (h - 37) * 2 / 3 - 16 - 34 - 4
                        onChange(container.offsetWidth, ch)

                        fun onResize(e: Event) {
                            window.requestAnimationFrame {
                                val h = document.body?.offsetHeight ?: 0
                                val ch = (h - 37) * 2 / 3 - 16 - 34 - 4
                                onChange(container.offsetWidth, ch)
                            }
                        }
                        window.addEventListener("resize", ::onResize)
                        onDispose { window.removeEventListener("resize", ::onResize) }
                    }
                })
            }
        }
    }
}

data class DomPosition(
    val isBuy: Boolean,
    val limit: BigDecimal,
    val limitSize: BigDecimal,
    val subtotal: BigDecimal,
    val totalSize: BigDecimal
)

@Composable
fun DOMChart(app: App, user: User, pair: CurrencyPair) {
    var data by remember { mutableStateOf<DepthOfMarket?>(null) }

    LaunchedEffect(pair) {
        Trader.depthOfMarketFlow(pair).onEach {
            data = it
            println("------------ Dom positions ----------------")
            data?.buyPositions?.forEach {
                println("buy: ${it.count} @${it.limit}")
            }
            data?.sellPositions?.forEach {
                println("sell: ${it.count} @${it.limit}")
            }
            println("------------ Dom positions END ----------------")
        }.collect()
    }

    @Composable
    fun Header(content: @Composable () -> Unit) {
        Th(attrs = { classList("sticky top-0 z-10 backdrop-blur backdrop-filter bg-gray-50 whitespace-nowrap px-2 py-1 text-left text-sm font-semibold text-gray-900") }) {
            content()
        }
    }

    @Composable
    fun OrderPosition(p: DomPosition) {
        val bgColor = if (p.isBuy) "#dcfce7" else "#fee2e2"
        val textColor = if (p.isBuy) "text-green-500" else "text-red-500"

        val bgWidth = BigDecimal.fromDouble(100.0) - p.subtotal.divide(
            p.totalSize,
            DecimalMode(8, RoundingMode.ROUND_HALF_TO_EVEN)
        ) * 100

        Tr(attrs = {
            style {
                backgroundImage("linear-gradient(to right, white, white)")
                backgroundSize("$bgWidth% 100%")
                backgroundColor(Color(bgColor))
                backgroundRepeat("no-repeat")
            }
        }) {
            Td(attrs = { classList("whitespace-nowrap $textColor font-bold py-1 pl-4 pr-3 text-sm text-gray-900 sm:pl-6") }) {
                Text(d(p.limit, pair.quotePrecision))
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-900") }) {
                Text(d(p.limitSize, pair.basePrecision))
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-900") }) {
                Text(d(p.subtotal, pair.basePrecision))
            }
        }
    }

    fun amountTotal(list: List<BigDecimal>): BigDecimal {
        var total = BigDecimal.ZERO
        for (b in list) {
            total += b
        }

        return total
    }

    data?.let { it ->
        val lastRate = it.lastRate
        val lastChangeDirection = it.lastChangeDirection
//        val limitOrders = it.orders.filter { it.limit != null }
        val positions = mutableListOf<DomPosition>()

        fun createPositions(isBuy: Boolean, pos: List<DepthOfMarket.Position>) {
            val total = amountTotal(pos.map { it.amount })
            var subtotal = BigDecimal.ZERO

            pos.sortedBy { if( isBuy) -it.limit else it.limit }.forEach {
                subtotal += it.amount

                positions.add(
                    DomPosition(
                        isBuy,
                        it.limit,
                        it.amount,
                        subtotal,
                        total
                    )
                )
            }
        }

        createPositions(true, it.buyPositions)
        createPositions(false, it.sellPositions)

//        val byType = limitOrders.groupBy { it.isBuy }

//        for (entry in byType) {
//            val isBuy = entry.key
//            val ordersByType = entry.value
//            val totalSize = amountTotal(ordersByType)
//            val byLimit = ordersByType.groupBy { it.limit }
//
//            var subtotal = BigDecimal.ZERO
//            var limits = byLimit.keys.toList().sortedBy { it }
//            if (isBuy) limits = limits.reversed()
//
//            limits.forEach {
//                it?.let { limit ->
//                    byLimit[limit]?.let { orders ->
//                        val limitSize = amountTotal(orders)
//                        subtotal += limitSize
//
//                        positions.add(
//                            DomPosition(
//                                isBuy,
//                                limit,
//                                limitSize,
//                                subtotal,
//                                totalSize
//                            )
//                        )
//                    }
//                }
//            }
//        }

        val buyPositions = positions.filter { it.isBuy }.sortedBy { -it.limit }
        val sellPositions = positions.filter { !it.isBuy }.sortedBy { it.limit }
        var spread: BigDecimal? = null
        if (buyPositions.size > 0 && sellPositions.size > 0) {
            spread = sellPositions.minOf { it.limit } -
                    buyPositions.maxOf { it.limit }
        }

        Div(attrs = { classList("shadow ring-1 ring-black ring-opacity-5 md:rounded-lg") }) {
            Table(attrs = { classList("min-w-full divide-y divide-gray-300") }) {
                Thead(attrs = { classList("bg-gray-50") }) {
                    Tr {
                        Th(attrs = { classList("sticky top-0 z-10 backdrop-blur backdrop-filter bg-gray-50 whitespace-nowrap py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6") }) {
                            Text("Price(${displayCurrencyCode(pair.quote)})")
                        }
                        Header { Text("Size(${displayCurrencyCode(pair.base)})") }
                        Header { Text("Sum(${displayCurrencyCode(pair.base)})") }
                    }
                }
                Tbody(attrs = { classList("divide-y divide-gray-200 bg-white") }) {
                    sellPositions.reversed().forEach { OrderPosition(it) }

                    Tr(attrs = { classList("border-t border-gray-200") }) {
                        Th(attrs = {
                            colspan(3)
                            classList("bg-gray-50 px-4 py-1 text-left text-sm font-semibold text-gray-900 sm:px-6")
                        }) {
                            // todo: new logic for market direction -> last deal type.
                            Div(attrs = { classList("flex items-center justify-between") }) {
                                lastChangeDirection?.let {
                                    when {
                                        lastChangeDirection == 0 -> {
                                            Span(attrs = { classList("text-black-500 text-lg inline-flex items-center") }) {
                                                lastRate?.let { Text(d(it, pair.quotePrecision)) }
                                            }
                                        }

                                        lastChangeDirection > 0 -> {
                                            Span(attrs = { classList("text-green-500 text-lg inline-flex items-center") }) {
                                                lastRate?.let { Text(d(it, pair.quotePrecision)) }
                                                ArrowUpSm()
                                            }
                                        }

                                        lastChangeDirection < 0 -> {
                                            Span(attrs = { classList("text-red-500 text-lg inline-flex items-center") }) {
                                                lastRate?.let { Text(d(it, pair.quotePrecision)) }
                                                ArrowDownSm()
                                            }
                                        }
                                    }
                                }

                                spread?.let {
                                    Span(attrs = { classList("text-lg") }) {
                                        Text("Spread: ${d(it, pair.quotePrecision)}")
                                    }
                                }
                            }
                        }
                    }
                    buyPositions.forEach { OrderPosition(it) }
                }
            }
        }
    }
}

@Composable
fun EditOrderModal(
    app: App,
    user: User,
    pair: CurrencyPair,
    order: TradeOrder,
    onUpdated: (order: TradeOrder) -> Unit,
    onClose: () -> Unit
) {
    Modal(onClose = { onClose() }) {
        OrderForm(app, user, pair, order,
            null,
            null,
            { onUpdated(it) },
            { onClose() }
        )
    }
}

@Composable
fun MyOrders(
    app: App,
    user: User,
    orders: List<TradeOrder>,
    pair: CurrencyPair,
    hasMore: Boolean,
    onUpdated: (order: TradeOrder) -> Unit
) {
    var isEditing by remember { mutableStateOf(false) }
    var isInfoOpened by remember { mutableStateOf(false) }
    var currentOrder: TradeOrder? by remember { mutableStateOf(null) }

    fun edit(o: TradeOrder) {
        currentOrder = o
        isEditing = true
    }

    fun info(o: TradeOrder) {
        currentOrder = o
        isInfoOpened = true
    }

    @Composable
    fun Header(content: @Composable () -> Unit) {
        Th(attrs = { classList("sticky top-0 z-10 backdrop-blur backdrop-filter bg-gray-50 whitespace-nowrap px-2 py-1 text-left text-sm font-semibold text-gray-900") }) {
            content()
        }
    }

    @Composable
    fun Status(colorClass: String, text: String, onRefresh: (suspend () -> Unit)? = null) {
        val scope = rememberCoroutineScope()
        var isLoading by mutableStateOf(false)

        fun refresh() {
            scope.launch {
                isLoading = true
                onRefresh?.let { it() }
                isLoading = false
            }
        }

        if (onRefresh == null) {
            Span(attrs = {
                classList("inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${colorClass}")
            }) {
                Text(text)
            }
        } else {
            Button(attrs = {
                classList("inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${colorClass}")
                onClick {
                    refresh()
                }
                if (isLoading) disabled()
            }) {
                if (isLoading) {
                    Text(text)
                    RefreshIcon("animate-spin ml-1 h-4 w-4")
                } else {
                    Text(text)
                    RefreshIcon("ml-1 h-4 w-4")
                }
            }
        }
    }

    @Composable
    fun OrderRow(order: TradeOrder) {
        val scope = rememberCoroutineScope()
        val status = order.status
        val isProcessing = status.isSuitableForPorcessing

        Tr {
            Td(attrs = { classList("whitespace-nowrap py-1 pl-4 pr-3 text-sm text-gray-500 sm:pl-6") }) {
                Text(order.id.toString())
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                if (order.isBuy) Span(attrs = { classList("text-green-500") }) { Text("Buy") }
                else Span(attrs = { classList("text-red-500") }) { Text("Sell") }
                Span(attrs = { classList("sr-only") }) { Text(order.id.toString()) }
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                // todo: actual precision should depend on buy/sell order type
                Text(
                    "${d(order.amount, pair.basePrecision)}${
                            order.initialAmount
                                ?.let { " / ${d(it, pair.basePrecision)}" } 
                    }"
                )
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                val limit = order.limit
                val aonTitle = if (order.isAllOrNothing) ":A/N" else ""
                // FIXME: let/run is not working
                if (limit == null) Text("market")
                else Text("${d(limit, pair.quotePrecision)}$aonTitle")
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                val ifClause = order.stop?.let { cond ->
                    if (status == TradeOrder.Status.ACTIVE)
                        " met: $cond"
                    else
                        " when: $cond"
                } ?: ""
                Text(ifClause)
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                order.expiresAt?.let { Text(DateTimeDisplay(it)) }
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                var colorClass = when (status) {
                    TradeOrder.Status.NEW -> "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    TradeOrder.Status.ACTIVE -> "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    TradeOrder.Status.EXECUTED -> "bg-green-100 text-green-800 hover:bg-green-200"
                    TradeOrder.Status.EXPIRED -> "bg-red-100 text-red-800 hover:bg-red-200"
                    TradeOrder.Status.REJECTED -> "bg-red-100 text-red-800 hover:bg-red-200"
                    TradeOrder.Status.REMOVED -> "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }
                Status(colorClass, status.toString())
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                if (
                    status != TradeOrder.Status.EXECUTED &&
                    status != TradeOrder.Status.REJECTED &&
                    status != TradeOrder.Status.REMOVED
                ) TLink("Edit") { edit(order) }
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-1 text-sm text-gray-500") }) {
                if (isProcessing) TLink("Delete") {
                    scope.launch {
                        try {
                            Trader.removeOrder(order)
                        } catch (e: ApiException) {
                            println("Failed to remove order ${e.text}")
                        }
                    }
                }
            }
            Td(attrs = { classList("relative whitespace-nowrap py-1 pl-3 pr-4 text-right text-sm font-medium sm:pr-6") }) {
                TLink({ InformationCircle() }) {
                    info(order)
                }
            }
        }
    }

    Div(attrs = { classList("shadow ring-1 ring-black ring-opacity-5") }) {
        Table(attrs = { classList("min-w-full divide-y divide-gray-300") }) {
            Thead(attrs = { classList("bg-gray-50") }) {
                Tr {
                    Th(attrs = { classList("sticky top-0 z-10 backdrop-blur backdrop-filter bg-gray-50 whitespace-nowrap py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6") }) {
                        Text("ID")
                    }
                    Header { Text("Operation") }
                    Header { Text("Amount") }
                    Header { Text("Limit") }
                    Header { Text("Stop") }
                    Header { Text("Expires at") }
                    Header { Text("Status") }
                    Header { }
                    Header { }
                    Th(attrs = { classList("sticky top-0 z-10 backdrop-blur backdrop-filter bg-gray-50 whitespace-nowrap py-1 pl-3 pr-4 sm:pr-6") }) {

                    }
                }
            }
            Tbody(attrs = { classList("divide-y divide-gray-200 bg-white") }) {
                orders.sortedBy { it.id }.reversed().forEach { OrderRow(it) }
            }
        }
    }

    if (isEditing) currentOrder?.let {
        EditOrderModal(app, user, pair, it, {
            onUpdated(it)
            isEditing = false
        }, { isEditing = false })
    }

    if (isInfoOpened) currentOrder?.let {
        Modal({ isInfoOpened = false }, size = "sm:max-w-6xl") {
            TradeOrderInfo(it)
        }
    }
}

@Composable
fun TradeOrderInfo(order: TradeOrder) {
    var logs: List<TradeOrder.Log> by remember { mutableStateOf(emptyList()) }

    LaunchedEffect(true) {
        val logResult = order.log()
        logs = logResult.records
    }

    @Composable
    fun Header(content: @Composable () -> Unit) {
        Th(attrs = { classList("whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900") }) {
            content()
        }
    }

    @Composable
    fun LogRow(log: TradeOrder.Log) {
        Tr {
            Td(attrs = { classList("whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:pl-6") }) {
                Text(DateTimeDisplay(log.createdAt))
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-2 text-sm text-gray-500") }) {
                Text("${log.type}")
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-2 text-sm text-gray-500") }) {
                Text(log.amount?.toPlainString() ?: "")
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-2 text-sm text-gray-500") }) {
                Text(log.rate?.toPlainString() ?: "")
            }
            Td(attrs = { classList("whitespace-nowrap px-2 py-2 text-sm text-gray-500") }) {
                Text(log.text ?: "")
            }
        }
    }

    Table(attrs = { classList("min-w-full divide-y divide-gray-300") }) {
        Thead(attrs = { classList("bg-gray-50") }) {
            Tr {
                Th(attrs = { classList("whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6") }) {
                    Text("Created at")
                }
                Header { Text("Type") }
                Header { Text("Amount") }
                Header { Text("Rate") }
                Header { Text("Message") }
            }
        }
        Tbody(attrs = { classList("divide-y divide-gray-200 bg-white") }) {
            logs.forEach { LogRow(it) }
        }
    }
}

@Composable
fun OperationTypeSelector(isBuy: Boolean, onChange: (newType: Boolean) -> Unit) {
    @Composable
    fun OperationTypeTab(isBuy: Boolean, isActive: Boolean) {
        val operationTitle = if (isBuy) "buy" else "sell"
        val title = "New $operationTitle order"
        val linkCSS =
            if (isActive) "border-indigo-500 text-indigo-600" else "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"

        A(null, attrs = {
            classList("$linkCSS w-1/2 py-1 px-1 text-center border-b-2 font-medium text-sm")
            if (!isActive) onClick { onChange(isBuy) }
        }) { Text(title) }
    }

    Div(attrs = { classList("border-b border-gray-200") }) {
        Nav(attrs = {
            classList("-mb-px flex")
            attr("aria-label", "Tabs")
        }) {
            OperationTypeTab(true, isBuy)
            OperationTypeTab(false, !isBuy)
        }
    }
}


@Composable
fun MarketUI(app: App, user: User) {
    val pairs = app.pairs
    val pairKey = "market_pair"
    val defaultPair = app.api.storage.read<CurrencyPair>(pairKey) ?: pairs.first()
    var pair: CurrencyPair by remember { mutableStateOf(defaultPair) }

    var rate: StatSampleRate by remember { mutableStateOf(StatSampleRate.DAYS) }

    var width by remember { mutableStateOf(100) }
    var height by remember { mutableStateOf(100) }

    var orders by remember { mutableStateOf<List<TradeOrder>>(emptyList()) }
    var orderUpdates by remember { mutableStateOf<Map<Long, TradeOrder>>(emptyMap()) }
    var hasMoreOrders by remember { mutableStateOf(false) }

    fun applyUpdate(order: TradeOrder, update: TradeOrder? = null) {
        val newOrder = update ?: orderUpdates.get(order.id)

        newOrder?.let { updated ->
            println("Apply update ${updated.status}")
            val mutableOrders = orders.toMutableList()
            mutableOrders.find { it.id == order.id }?.let { replace ->
                println("found existing order ${replace.id}")
                mutableOrders.set(mutableOrders.indexOf(replace), updated)
                orders = mutableOrders.toList()
            }
        }
    }

    LaunchedEffect(true) {
        val flow = Trader.orderChangeFlow()
        flow.onEach { update ->
            println("Received order update id ${update.id}, status = ${update.status}")
            if (orders.isEmpty()) {
                val mutableUpdates = orderUpdates.toMutableMap()
                mutableUpdates.put(update.id, update)
                orderUpdates = mutableUpdates
            } else {
//                val existing = orders.find { it.id == update.id }
//                if (existing == null) {
//                    orders = listOf(update) + orders
//                }
//                else applyUpdate(update, update)
                applyUpdate(update, update)
            }
        }.collect()
    }

    LaunchedEffect(pair) {
//        Trader.orderChangeFlow().collect { update ->
//            if (orders.isEmpty()) {
//                val mutableUpdates = orderUpdates.toMutableMap()
//                mutableUpdates.put(update.id, update)
//                orderUpdates = mutableUpdates
//            } else {
//                applyUpdate(update, update)
//            }
//        }

        var tradeOrders = Trader.getOrders(pair, null, false, 50)
        println("LOADED ORDERS ${tradeOrders.tradeOrders.size}")
        orders = tradeOrders.tradeOrders
        hasMoreOrders = tradeOrders.hasMore

        orders.forEach { applyUpdate(it) }
    }

    ChartChartResizer { w, h -> width = w; height = h }

    Div(attrs = {
        classList("flex flex-row h-screen max-h-screen")
        style {
            position(Position.Absolute)
            width(100.percent)
            left(0.percent)
            top(0.percent)
            paddingTop(37.px)
            overflow("hidden")
        }
    }) {
        Div(attrs = { classList("basis-3/4") }) {
            Div(attrs = { classList("bg-white max-h-2/3 h-2/3 overflow-y-auto border-gray-200") }) {
                Div(attrs = {
                    classList("px-3 py-2 sm:px-3 overflow-scroll")
                }) {
                    TForm {
                        Div(attrs = { classList("grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6") }) {
                            Div(attrs = { classList("sm:col-span-3") }) {
                                CurrencyPairSelect("pair", pair) {
                                    options(pairs)
                                    label { Text("Select currency pair") }
                                    onChange {
                                        it?.let {
                                            pair = it
                                            app.api.storage.write(pairKey, it)
                                        }
                                    }
                                    condensed()
                                }
                            }
                            Div(attrs = { classList("sm:col-span-3") }) {
                                StatSampleRateSelect("rate", rate) {
                                    label { Text("Select interval") }
                                    onChange { it?.let { rate = it } }
                                    condensed()
                                }
                            }
                        }
                    }

                    Div(attrs = { classList("mt-1") }) {
                        CandleChartLoader(
                            pair,
                            rate,
                            ChartSize(width, height)
                        )
                    }
                }
            }

            Div(attrs = { classList("max-h-1/3 h-1/3 overflow-y-auto border-gray-200 border-t") }) {
                MyOrders(app, user, orders, pair, hasMoreOrders) {
                    orders += it
                    applyUpdate(it)
                }
            }
        }
        Div(attrs = { classList("basis-1/4") }) {
            Div(attrs = { classList("max-h-2/3 h-2/3 overflow-y-auto border-gray-200 border-l") }) {
                DOMChart(app, user, pair)
            }
            Div(attrs = { classList("max-h-1/3 h-1/3 overflow-y-auto border-gray-200 border-t border-l") }) {
                BuySell(app, user, pair, orders) {
                    println("CREATED ORDER ${it.id}")
                    orders += it
                    applyUpdate(it)
                }
            }
        }
    }
}

enum class ExpiresDuration(val duration: Duration, val label: String) {
    MIN30(30.minutes, "30 min"),
    HOUR(1.hours, "1 hour"),
    HOUR12(12.hours, "12 hours"),
    DAY(1.days, "1 day"),
    WEEK(7.days, "1 week"),
    WEEK2(14.days, "2 weeks"),
    MONTH(28.days, "4 weeks"),
    MONTH2(56.days, "8 weeks")
}

@Composable
fun expiresAtSelector2(duration: Duration, onChange: (d: Duration) -> Unit) {
    val chunks = ExpiresDuration.values().toList().chunked(4)

    Div {
        Label(attrs = { classList("block text-sm font-medium text-gray-700") }) {
            Text("Expires at")
        }

        Fieldset {
            for (chunk in chunks) {
                Div(attrs = { classList("flex flex-row space-x-4") }) {
                    for (value in chunk) {
                        val id = "expires-${value.ordinal}"
                        Div(attrs = { classList("basis-1/4 mt-4 flex") }) {
                            Input(InputType.Radio) {
                                id(id)
                                name("expiresAt")
                                classList("focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300")
                                checked(duration == value.duration)
                                onChange { onChange(value.duration) }
                            }
                            Label(id, attrs = { classList("ml-3 block text-sm font-medium text-gray-700") }) {
                                Text(value.label)
                            }
                        }
                    }
                }
            }
        }
    }
}

fun divideBigDecimal(a: BigDecimal, b: BigDecimal): BigDecimal {
    return a.divide(b, DecimalMode(18, RoundingMode.ROUND_HALF_TO_EVEN))
}

fun getDefaultDuration(order: TradeOrder): ExpiresDuration {
    val expiresAt = order.expiresAt
    if (expiresAt == null) return ExpiresDuration.WEEK

    return ExpiresDuration.values().find {
        ((expiresAt - order.createdAt) - it.duration).absoluteValue < 5.minutes
    } ?: ExpiresDuration.WEEK
}

fun d(num: BigDecimal, precision: Int): String {
    val t = num.scale(precision.toLong()).toPlainString()
    val (i,d) = t.split(".")
    val i1 = StringBuilder()
    for( (pos, x) in i.reversed().withIndex()) {
        if (pos % 3 == 0 && pos != 0) {
            i1.append(",")
        }
        i1.append(x)
    }
    return "${i1.reverse()}.$d"
}

@Composable
fun OrderForm(
    app: App,
    user: User,
    pair: CurrencyPair,
    order: TradeOrder?,
    myOrders: List<TradeOrder>? = null,
    onCreate: ((order: TradeOrder) -> Unit)?,
    onUpdated: ((order: TradeOrder) -> Unit)? = null,
    onCancel: (() -> Unit)? = null,
) {
    var isBuy by remember { mutableStateOf(order?.isBuy ?: true) }
    val isEditing = order != null
    val isEditingLimit = order?.limit != null
    val defaultType = if (!isEditing || isEditingLimit) TradeOrderType.LIMIT else TradeOrderType.MARKET
    val defaultExpiresDuration = if (order != null) getDefaultDuration(order) else ExpiresDuration.WEEK
    var stopCondition: String? by remember { mutableStateOf(order?.stop) }
    var orderType by remember { mutableStateOf(defaultType) }
    var limit by remember { mutableStateOf(order?.limit) }
    var amount by remember { mutableStateOf(order?.amount) }
    var expiresAtDuration by remember { mutableStateOf(defaultExpiresDuration) }
    var aon by remember { mutableStateOf(order?.isAllOrNothing ?: false) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var baseAccount: ApiAccount? by remember { mutableStateOf(null) }
    var quoteAccount: ApiAccount? by remember { mutableStateOf(null) }
    var baseAccountLoaded by mutableStateOf(baseAccount != null)
    var quoteAccountLoaded by mutableStateOf(quoteAccount != null)

    var data by remember { mutableStateOf<OrderbookSnapshot?>(null) }
    var lastBid: BigDecimal? by remember { mutableStateOf(null) }
    var lastAsk: BigDecimal? by remember { mutableStateOf(null) }

    LaunchedEffect(pair) {
        baseAccount = getDefaultAccount(user, pair.base) ?: user.createAccount(pair.base)
        quoteAccount = getDefaultAccount(user, pair.quote) ?: user.createAccount(pair.quote)
    }

    if (!isEditing) LaunchedEffect(pair) {
        lastAsk = null
        lastBid = null
        val x = Trader.orderbookFor(pair)
        x.onEach {
            data = it
        }.collect()
    }

    data?.let { it ->
        val limitOrders = it.orders.filter { it.limit != null }
        val byType = limitOrders.groupBy { it.isBuy }

        for (entry in byType) {
            val isBuy = entry.key
            val ordered = entry.value.sortedBy { it.limit }
            if (isBuy) lastAsk = ordered.lastOrNull()?.limit
            else lastBid = ordered.firstOrNull()?.limit
        }
    }

    @Composable
    fun Account(currency: Currency, selected: ApiAccount?, precision: Int, onChange: (account: ApiAccount?) -> Unit) {
        val filtered = user.accounts.filter { it.currencyCode == currency.code }
        var freeBalance: BigDecimal? by remember { mutableStateOf(null) }

        LaunchedEffect(selected) {
            selected?.let {
                if (freeBalance != null) freeBalance = null
                if (app.checkBalance) {
                    freeBalance = it.getAvailableBalance()
                    println(
                        "Available balance for account ID ${it.id} (${it.currencyCode}) is ${
                            it.getAvailableBalance().toPlainString()
                        }"
                    )
                }
            }
        }

        if (filtered.size == 0) {

        } else if (filtered.size == 1) {
            selected?.let {
                val acc = it
                Span(attrs = { classList("text-xs") }) {
                    if (app.checkBalance) freeBalance?.let { Text("Avlbl ${d(it, precision)} ${displayCurrencyCode(currency)}") }
                    else Text("Avlbl ${d(acc.balance, precision)} ${displayCurrencyCode(currency)}")
                }
            }
        } else {
            AccountSelectByCurrency(
                "acc",
                user,
                currency,
                selected
            ) {
                label { Text("Select ${displayCurrencyCode(currency)} account") }
                onChange { onChange(it) }
                condensed()
            }

            Div(attrs = { classList("mt-1 text-sm") }) {
                selected?.let {
                    if (app.checkBalance) freeBalance?.let { Text(" ${d(it, precision)} ${displayCurrencyCode(currency)}") }
                    else Text("Avlbl: ${d(it.balance, precision)} ${displayCurrencyCode(currency)}")
                }
            }
        }
    }

    fun stop(e: String) {
        error = e
        isLoading = false
    }

    fun update(amount: BigDecimal?) {
        isLoading = true
        error = ""

        if (order == null) return stop("")

        val isLimit = orderType == TradeOrderType.LIMIT
        if (isLimit && (limit == BigDecimal.ZERO || limit == null)) return stop("Limit can't be empty")
        if (amount == BigDecimal.ZERO || amount == null) return stop("amount can't be zero")

        val newOrder = order.copy(
            limit = limit,
            amount = amount,
            isAllOrNothing = aon,
            stop = stopCondition,
            expiresAt = Clock.System.now() + expiresAtDuration.duration
        )

        GlobalScope.launch {
            try {
                val updated = Trader.updateOrder(newOrder)
                if (onUpdated != null) {
                    onUpdated(updated)
                }
            } catch (err: ApiException) {
                error = err.text
            }
            isLoading = false
        }
    }

    fun create(isBuy: Boolean, baseAccount: ApiAccount?, quoteAccount: ApiAccount?, amountValue: BigDecimal?) {
        // todo: make it always work
        isLoading = true
        error = ""
        val isLimit = orderType == TradeOrderType.LIMIT

        if (baseAccount == null) return stop("Select base account")
        if (quoteAccount == null) return stop("Select quote account")
        if (isLimit && (limit == BigDecimal.ZERO || limit == null)) return stop("Limit can't be empty")
        if (amountValue == BigDecimal.ZERO || amountValue == null) return stop("amount can't be zero")

        println("isBuy: $isBuy, baseAccount: $baseAccount, quoteAccount: $quoteAccount, amountValue: $amountValue")

        suspend fun checkBalance(): Boolean {
            var isOk = true
            val appBalance = if (isBuy) quoteAccount.balance else baseAccount.balance
            val availableBalance = if (app.checkBalance) {
                if (isBuy) quoteAccount.getAvailableBalance() else baseAccount.getAvailableBalance()
            } else {
                if (isBuy) quoteAccount.balance else baseAccount.balance
            }

            if (!app.checkBalance || !isLimit) return isOk

            limit?.let {
                val limitDecimal = it
                val total = if (isBuy) amountValue * limitDecimal else amountValue
                println("balance is ${availableBalance.toPlainString()}, app balance is ${appBalance.toPlainString()}, total is ${total.toPlainString()}")

                if (total > availableBalance) isOk = false
            }

            if (!isBuy && availableBalance < amountValue) isOk = false

            return isOk
        }

        val buyAccount = if (isBuy) baseAccount else quoteAccount
        val sellAccount = if (isBuy) quoteAccount else baseAccount
        val availableBalance = if (isBuy) quoteAccount.balance else baseAccount.balance
//        val frozenBalance = if (isBuy) quoteAccount.getFrozenBalance()

//        if (isLimit) {
//            limit?.let {
//                val limitDecimal = it
//                val total = if (isBuy) amountValue * limitDecimal else amountValue
//                println("balance is $availableBalance, total is $total")
//
//                if (total > availableBalance) return stop("Not sufficient funds")
//            }
//        }
//
//        if (!isBuy && availableBalance < amountValue) return stop("Not sufficient funds")

        fun operationLabel(isBuy: Boolean): String {
            return if (isBuy) "Buy" else "Sell"
        }

        val collision1 =
            myOrders?.find { it.isBuy == !isBuy && it.status.isSuitableForPorcessing && (it.limit == null || limit == null) }
        if (collision1 != null) return stop("Collision found: your ${operationLabel(!isBuy)} order #${collision1.id} contradicts new one")

        fun getLimitCollision(a: TradeOrder, bIsBuy: Boolean, bLimit: BigDecimal?): Boolean {
            if (a.isBuy == bIsBuy || !a.status.isSuitableForPorcessing) return false

            val aLimit = a.limit

            if (aLimit == null || bLimit == null) return false

            if (a.isBuy && bLimit - aLimit < 0) return true
            if (bIsBuy && aLimit - bLimit < 0) return true

            return false
        }

        val collision2 = myOrders?.find {
            getLimitCollision(it, isBuy, limit)
        }

        if (collision2 != null) return stop("Collision found: your ${operationLabel(!isBuy)} order #${collision2.id} produces negative spread with new one")

        GlobalScope.launch {
            try {
                val isBalanceOk = checkBalance()

                if (!isBalanceOk) stop("Insufficient funds")
                else {
                    println("Try to create order isBuy = ${isBuy}, buyAccount = ${buyAccount.currencyCode}, sellAccount = ${sellAccount.currencyCode}")
                    val order = Trader.createOrder(
                        pair,
                        isBuy,
                        amountValue,
                        if (isLimit) limit else null,
                        null,
                        aon,
                        buyAccount,
                        sellAccount,
                        Clock.System.now() + expiresAtDuration.duration
                    )

                    limit = null
                    amount = null

                    if (onCreate != null) {
                        onCreate(order)
                    }
                }
            } catch (err: ApiException) {
                error = err.text
            }
            isLoading = false
        }
    }

    Form(
        attrs = {
            classes("space-y-1")
            method(FormMethod.Post)
            action("javascript:void(0);")
        }
    ) {
//        if (!isEditing) OperationTypeSelector(isBuy) { isBuy = it }

        if (!isEditing) Div(attrs = { classList("grid gap-x-1 grid-cols-2") }) {
            TradeOrderTypeSelect("orderType", orderType, attrs = {
                onChange {
                    it?.let {
                        orderType = it
                        if (it == TradeOrderType.MARKET) limit = null
                    }
                }
                condensed()
            })

            TCheckbox("aon", aon) {
                onChange { aon = it }
                label { Text("All or None") }
            }
        }

        Div(attrs = { classList("grid gap-x-1 grid-cols-2") }) {
            TDecimalInput("limit", limit) {
                onChange { limit = it }
                onInput { limit = it }
                label { Text("Price ${displayCurrencyCode(pair.quote)}") }
                placeholder = if (orderType == TradeOrderType.MARKET) "MARKET" else ""
                condensed()
                precision(pair.quotePrecision.toLong())
                if (orderType == TradeOrderType.MARKET) disabled()
                if (orderType == TradeOrderType.LIMIT) {
                    limit?.let { l ->
                        amount?.let { a ->
                            val total = a * l
                            underInput("≈ ${d(total, pair.quotePrecision)} ${displayCurrencyCode(pair.quote)}")
                        }
                    }
                }
            }

            TDecimalInput("amount", amount) {
                label { Text("Amount ${displayCurrencyCode(pair.base)}") }
                onChange { it?.let { amount = it } ?: run { amount = BigDecimal.ZERO } }
                onInput { amount = it }
                condensed()
                precision(pair.basePrecision.toLong())
            }
        }

        if (isEditing) TCheckbox("aon", aon) {
            onChange { aon = it }
            label { Text("All or None") }
        }

        if (!isEditing) {
            Div(attrs = { classList("grid gap-x-1 grid-cols-2") }) {
                Div {
                    Div {
                        Span(attrs = { classList("text-xs") }) {
                            lastBid?.let { Text("Bid ${d(it, pair.quotePrecision)} ${displayCurrencyCode(pair.quote)}") }
                        }
                    }
                    Account(pair.quote, quoteAccount, pair.quotePrecision) {
                        quoteAccount = it
                        quoteAccountLoaded = true
                    }
                }
                Div {
                    Div {
                        Span(attrs = { classList("text-xs") }) {
                            lastAsk?.let { Text("Ask ${d(it, pair.basePrecision)} ${displayCurrencyCode(pair.quote)}") }
                        }
                    }
                    Account(pair.base, baseAccount, pair.basePrecision) {
                        baseAccount = it
                        baseAccountLoaded = true
                    }
                }
            }
        }

        ExpiresAtSelect("expiresAt", expiresAtDuration) {
            onChange { it?.let { expiresAtDuration = it } }
        }

        if (error != "") P(attrs = { classList("text-red-600 text-xs") }) { Text(error) }

        Div(attrs = { classList("mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense") }) {
            if (!isEditing) {
                if (baseAccountLoaded && quoteAccountLoaded) {
                    TButtonWide(attrs = {
                        size(ButtonSize.base)
                        color(ButtonColor.Green)
                        onClick { create(true, baseAccount, quoteAccount, amount) }
                        isLoading(isLoading)
                    }) { Text("Buy ${displayCurrencyCode(pair.base)}") }
                    TButtonWide(attrs = {
                        size(ButtonSize.base)
                        color(ButtonColor.Red)
                        onClick { create(false, baseAccount, quoteAccount, amount) }
                        isLoading(isLoading)
                    }) { Text("Sell ${displayCurrencyCode(pair.base)}") }
                }
            } else {
                TButtonWide(attrs = {
                    size(ButtonSize.base)
                    color(ButtonColor.Indigo)
                    onClick { update(amount) }
                    isLoading(isLoading)
                }) { Text("Save changes") }

                TButtonWide(attrs = {
                    size(ButtonSize.base)
                    color(ButtonColor.Neutral)
                    onClick { if (onCancel != null) onCancel() }
                    isLoading(isLoading)
                }) { Text("Cancel") }
            }
        }
    }
}

fun getDefaultAccount(user: User, currency: Currency): ApiAccount? {
    val filtered = user.accounts.filter { it.currencyCode == currency.code }
    if (filtered.size == 1) return filtered.firstOrNull()

    val default = filtered.firstOrNull { it.isDefault }
    val first = filtered.firstOrNull()

    return default ?: first
}

@Composable
fun BuySell(
    app: App,
    user: User,
    pair: CurrencyPair,
    myOrders: List<TradeOrder>,
    onCreate: (order: TradeOrder) -> Unit
) {
    Div(attrs = {
        classList("bg-white shadow px-3 py-2 sm:px-3 overflow-scroll")
    }) {
        OrderForm(app, user, pair, null, myOrders, onCreate)
    }
}

enum class TradeOrderType(val title: String, description: String) {
    LIMIT(
        "Limit",
        "Buy or sell a stock with a restriction on the maximum price to be paid or the minimum price to be received"
    ),
    MARKET("Market", "Market’s current best available price")
}

@Composable
fun OrderTypeSelector2(value: String, onChange: (newValue: String) -> Unit) {
    Label(attrs = { classList("block text-sm font-medium text-gray-700") }) {
        Text("Select order type")
    }

    Fieldset {
        Legend(attrs = { classList("sr-only") }) { Text("Select order type") }
        Div(attrs = { classList("space-y-5") }) {


            Div(attrs = { classList("relative flex items-start") }) {
                Div(attrs = { classList("flex items-center h-5") }) {
                    Input(InputType.Radio) {
                        id("stop_condition1")
                        name("stopCondition")
                        checked(value == "market")
                        classList("focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300")
                        onChange { onChange("market") }
                    }
                }
                Div(attrs = { classList("ml-3 text-sm") }) {
                    Label("stop_condition1", attrs = { classList("font-medium text-gray-700") }) { Text("Market") }
                    P(attrs = { classList("text-gray-500") }) { Text("Market’s current best available price") }
                }
            }

            Div(attrs = { classList("relative flex items-start") }) {
                Div(attrs = { classList("flex items-center h-5") }) {
                    Input(InputType.Radio) {
                        id("stop_condition2")
                        name("stopCondition")
                        checked(value == "limit")
                        classList("focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300")
                        onChange { onChange("limit") }
                    }
                }
                Div(attrs = { classList("ml-3 text-sm") }) {
                    Label("stop_condition2", attrs = { classList("font-medium text-gray-700") }) { Text("Limit") }
                    P(attrs = { classList("text-gray-500") }) { Text("Buy or sell a stock with a restriction on the maximum price to be paid or the minimum price to be received") }
                }
            }

        }
    }
}


@Composable
fun MarketScreenUI(app: App, user: User) {
    val scope = rememberCoroutineScope()
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }

    fun requestAccess() {
        isLoading = true
        error = ""

        GlobalScope.launch {
            try {
                val order = ExchangeAccessOrder(true)
                println("Run submit")
                val submitResult = order.submit()
                println("submit $submitResult")
                println("submit order ${submitResult.order}")
                val exchangeOrder = submitResult.order as ExchangeAccessOrder

                println("allowApiAccess = ${exchangeOrder.allowApiAccess}")
                println("status ${submitResult.lastKnownStatus}")
                println("status ${submitResult.lastKnownStatus.toString()}")
            } catch (e: ApiException) {
                error = e.text
            }
            isLoading = false
        }
    }

    MarketUI(app, user)

//    if (user.userAccount.canUseExchange) MarketUI(app, user)
//    else {
//        RoundedContainer {
//            Div(attrs = { classList("relative max-w-md mx-auto py-12 px-4 space-y-6 sm:max-w-3xl sm:py-16 sm:px-6 lg:max-w-none lg:p-0 lg:col-start-4 lg:col-span-6") }) {
//                H2(attrs = { classList("text-3xl font-extrabold") }) {
//                    Text("Exchange request needeed")
//                }
//
//                P(attrs = { classList("text-lg") }) {
//                    Text("You need to request access to perform market operations")
//                }
//
//                ErrorString(error)
//
//                TButton(attrs = {
//                    onClick { requestAccess() }
//                    isLoading(isLoading)
//                }) { Text("Request access") }
//            }
//        }
//    }
}
